<template>
  <div class="row" v-if="canSearch">
    <div class="col-md-12 card">
      <div class="card-body row">
        <div class="col-sm-12">
          <div class="pull-right">
            <el-input type="search" clearable class="input-sm" placeholder="Search Task Name" v-model="ui.searchQuery" aria-controls="datatables" style="width:250px; margin-right: 12px;" />
            <v-select
              v-model="ui.taskTemplateName"
              @input="setTaskTemplateName"
              placeholder="Task Template"
              style="width: 450px; display: inline-block; margin-right: 12px;"
              label="label"
              value="label"
              :options="uniqueTaskTemplates">
            </v-select>
            <el-select class="select select-filter"
                       name="departmentFilter"
                       size="large"
                       clearable
                       placeholder="Department"
                       v-model="ui.departmentName">
              <el-option v-for="option in uniqueDepartments"
                         class="select"
                         :value="option.label"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
            <button type="button" v-if="canEdit" @click="addTaskName" class="btn btn-fill btn-info btn-new-task-name">
              New Task Name
            </button>
          </div>
        </div>
        <div class="col-sm-12">
          <div v-if="loading" style="text-align: center;">
            <img src="static/img/loading.gif"/>
          </div>
          <el-table class="table-striped wrap-whole-words indexed-table-column-width"
                    v-else
                    :data="queriedData"
                    border
                    :max-height="tableHeight(340)"
                    style="width: 100%">
            <el-table-column
                :width="this.constants.INDEX_COLUMN_WIDTHS.WIDE"
                align="center"
                label="#"
                type="index">
            </el-table-column>
            <el-table-column
              :min-width="240"
              sortable
              :sort-method="(a, b) => callStringSort(a, b, 'name')"
              label="Name">
              <template v-slot="props">
                <span class="invisible">{{ props.row.id }}</span>
                <a href="#" @click.prevent="editTaskName(props.row)">{{ props.row.name }}</a>
              </template>
            </el-table-column>
            <el-table-column
              :min-width="240"
              sortable
              :sort-method="(a, b) => callStringSort(a, b, 'key')"
              label="Key">
              <template v-slot="props">
                {{ props.row.key }}
              </template>
            </el-table-column>
            <el-table-column
              :min-width="190"
              sortable
              :sort-method="(a, b) => callStringSort(a, b, 'taskTemplateName')"
              label="Task Template">
              <template v-slot="props">
                <a href="#" @click.prevent="editTaskTemplate(props.row.taskTemplateId)">{{ props.row.taskTemplateName }}</a>
              </template>
            </el-table-column>
            <el-table-column
              :min-width="170"
              sortable
              :sort-method="(a, b) => callStringSort(a, b, 'qbItemCode')"
              label="QB Item Code">
              <template v-slot="props">
                {{ props.row.qbItemCode }}
              </template>
            </el-table-column>
            <el-table-column
              :min-width="150"
              sortable
              :sort-method="(a, b) => callStringSort(a, b, 'qbClass')"
              label="QB Class">
              <template v-slot="props">
                {{ props.row.qbClass }}
              </template>
            </el-table-column>
            <el-table-column
              :min-width="170"
              sortable
              :sort-method="(a, b) => callStringSort(a, b, 'departmentNames')"
              label="Departments">
              <template v-slot="props">
                {{ props.row.departmentNames }}
              </template>
            </el-table-column>
            <el-table-column
              v-if="canEdit"
              width="105"
              fixed="right"
              label="Actions">
              <template v-slot="props">
                <p-button type="success" size="sm" icon>
                  <i class="fa fa-edit" @click="editTaskName(props.row)"></i>
                </p-button>
                <p-button type="danger" size="sm" icon @click="confirmDelete(props.row)">
                  <i class="fa fa-times"></i>
                </p-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {
  notifySuccess,
  stringSort,
  notifyError,
  getTableHeight,
  uniqueKvpsForSelectFromArray,
  uniqueKvpsForSelectFromArrayWithCsvValues,
  messageForSafeDelete
} from '@/utilities/utils'
import PButton from '@/components/UIComponents/Button.vue'
import Constants from '@/utilities/constants'
import Permissions from '@/utilities/permissions'
import {OK, NO_CONTENT} from 'http-status-codes'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import {sync} from 'vuex-pathify'

export default{
  components: {
    PButton,
    vSelect
  },
  computed: {
    ...mapGetters({
      taskTypes: 'tasks/templates/kvpsForSelect',
      departments: 'departments/kvpsForSelect',
      tableData: 'tasks/names/results'
    }),
    ui: sync('tasks/names@ui'),
    uniqueDepartments() {
      return uniqueKvpsForSelectFromArrayWithCsvValues(this.$store.getters['tasks/names/results'], 'departmentNames')
    },
    uniqueTaskTemplates() {
      return uniqueKvpsForSelectFromArray(this.$store.getters['tasks/names/results'], 'taskTemplateName')
    },
    queriedData() {
      if (!this.ui.searchQuery && !this.ui.taskTemplateName && !this.ui.departmentName) {
        return this.tableData
      }
      return this.tableData
        .filter((row) => {
          let isIncluded = false
          if (this.ui.searchQuery) {
            for (let key of this.propsToSearch) {
              if (row[key]) {
                let rowValue = row[key].toString().toLowerCase()
                if (rowValue.includes && rowValue.includes(this.ui.searchQuery.toLowerCase())) {
                  isIncluded = true
                }
              }
            }
          }

          if (this.ui.taskTemplateName && this.ui.taskTemplateName === row.taskTemplateName) {
            isIncluded = true
          }

          if (this.ui.departmentName && row.departmentNames.includes(this.ui.departmentName)) {
            isIncluded = true
          }

          return isIncluded
        })
    },
    canSearch() {
      return this.$can('read', Permissions.TASK_NAMES)
    },
    canEdit() {
      return this.$can('edit', Permissions.TASK_NAMES)
    }
  },
  data() {
    return {
      toDelete: null,
      propsToSearch: ['name', 'key'],
      constants: Constants,
      loading: false
    }
  },
  methods: {
    setTaskTemplateName(taskTemplate) {
      if (!taskTemplate) {
        this.$store.set('tasks/names/ui@taskTemplateName', null)
        return
      }
      this.$store.set('tasks/names/ui@taskTemplateName', taskTemplate.value)
    },
    callStringSort(a, b, prop) {
      return stringSort(a, b, prop)
    },
    tableHeight(buffer) {
      return getTableHeight(buffer)
    },
    addTaskName() {
      this.$store.commit('tasks/names/resetEdit')
      this.$eventHub.$emit('show-modal-1', 'Task Name', null, '600px')
    },
    editTaskName(taskName) {
      this.$eventHub.$emit('show-modal-1', 'Task Name', taskName, '600px')
    },
    editTaskTemplate(taskTemplateId) {
      this.$eventHub.$emit('show-modal-1', 'Task Template', {id: taskTemplateId}, '1100px')
    },
    async confirmDelete(taskName) {
      const responseTasks = await this.$store.dispatch('tasks/fetchExistsByNameId', taskName.id)

      if (OK !== responseTasks.status) {
        notifyError(this, 'Error determining if ' + taskName.name + ' is in use. Unable to delete task name.')
        return
      }

      const responsePriceRules = await this.$store.dispatch('priceRules/fetchFindByTaskNameId', taskName.id)

      if (OK !== responsePriceRules.status) {
        notifyError(this, 'Error determining if ' + taskName.name + ' is in use by price rules. Unable to delete task name.')
        return
      }

      let showButton = true
      let message = 'Are you sure you want to delete <strong>' + taskName.name + '</strong>? This will completely remove the task name.'

      const usedByPriceRules = responsePriceRules.data
      if (usedByPriceRules.length > 0) {
        message = '<strong>' + taskName.name + '</strong> is used by the following price rules and cannot be deleted:'
        message += '<p>'
        message = messageForSafeDelete(usedByPriceRules, 'shortId', message)
        message += '</p>'
        message += '<p>If you really want to delete this Task Name, you must first remove associations to it in each of the price rules listed above, then come back here and delete the Task Name.</p>'
        showButton = false
      }

      if (responseTasks.data) {
        message = '<strong>' + taskName.name + '</strong> is used by at least one task and cannot be deleted. You may inactivate it.'
        showButton = false
      }

      this.toDelete = taskName.id
      const payload = {
        message,
        entity: taskName,
        delete: this.delete,
        showButton
      }
      this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
    },
    async delete() {
      const response = await this.$store.dispatch('delete', {
        endpoint: 'task-names',
        id: this.toDelete
      })
      if (NO_CONTENT === response.status) {
        this.$store.set('tasks/names/delete', this.toDelete)
        notifySuccess(this, 'Task Name Deleted Successfully')
      } else {
        notifyError(this, 'Error Deleting Task Name')
      }
    },
    async fetchAllTaskNames() {
      await this.$store.dispatch('tasks/names/fetchAll')
    }
  },
  async beforeMount() {
    this.loading = true
    await Promise.all([
      this.$store.cache.dispatch('quickbooks/fetchAll', true),
      this.$store.cache.dispatch('tasks/templates/fetchAll'),
      this.$store.cache.dispatch('inspectorApp/formFieldBlocks/fetchAll')
    ])
    await this.fetchAllTaskNames()
    this.loading = false
    this.$eventHub.$on('taskNameSaved', this.fetchAllTaskNames)
  },
  destroyed() {
    this.$eventHub.$off('taskNameSaved')
  }
}
</script>

<style scoped>
  .pull-right .inline-block {
    display: inline-block;
    margin-right: 12px;
  }
  .action-icon {
    padding: 0 7px;
  }
  .row {
    margin-bottom: 15px;
  }
  .select-filter {
    margin-right: 12px;
    width: 185px;
  }
</style>
